import React, { useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './GoraiInfoPage.css';

// Info content
const infoContent = {
  index: {
    title: 'Strategic Summary',
    subtitle: 'Quick reference for developers and contributors',
    icon: '📋',
    content: `
## Core Positioning

### What We Are

- **Prosumer robotics framework** — real autonomy between educational toys ($100-300) and enterprise platforms ($4,000+)
- **Cloud-native architecture** — NATS, Prometheus, JetStream (battle-tested distributed systems)
- **Go-first, pragmatically polyglot** — Go core, Python/C++ services via NATS when appropriate

### What We're NOT

- ❌ **Not a ROS 2 replacement** — we target different markets (prosumer vs. enterprise/research)
- ❌ **Not language-purist** — we use the best tool for each job
- ❌ **Not anti-ROS 2** — bridge planned (Phase 3) for ecosystem compatibility
- ❌ **Not enterprise-focused** — we optimize for accessibility, not feature completeness

---

## Key Strategic Decisions

### 1. Language Strategy: Pragmatic Polyglot

**Decision**: Go core + polyglot services via NATS clients

| Component Type | Language | Rationale |
|----------------|----------|-----------|
| Framework core | Pure Go | Concurrency, deployment, AI-assisted coding |
| Simple sensors | Pure Go | GPIO, I2C, GPS, IMU — protocol parsing |
| Vision preprocessing | Python | OpenCV, scikit-image ecosystem |
| ML inference | Python or ONNX Runtime (C++) | PyTorch/TensorFlow training; ONNX deployment |
| SLAM | C++ with Go wrapper | Cartographer, ORB-SLAM too valuable to rewrite |
| Camera drivers | cgo wrappers | V4L2, RealSense SDKs in C/C++ |
| Motor controllers | Go or cgo | If SDK exists (Dynamixel), wrap; else pure Go |
| Web UI | Go templates + HTMX | Avoid separate JS frontend complexity |

**Why**: NATS has clients for 40+ languages. Any language can be a Gorai service.

### 2. ROS 2 Positioning: Coopetition, Not Competition

**Decision**: Build compatibility bridge, don't compete directly

**✅ DO:**
- Build ROS 2 bridge (Phase 3)
- Position as "ROS 2 for prosumers"
- Reuse ROS 2 message type designs
- Document ROS 2 migration paths
- Collaborate with ROS 2 community

**❌ DON'T:**
- Try to replace ROS 2 in research/enterprise
- Ignore ROS 2 ecosystem
- Start language wars
- Lock users into Gorai-only world

**Why ROS 2 Bridge Matters**:
1. **Component reuse** — use ROS 2 drivers when no Go alternative exists
2. **Simulation** — Gazebo integration via bridge
3. **Migration path** — students can keep ROS 2 packages they know
4. **Ecosystem perception** — avoid "vendor lock-in" perception

### 3. Deployment: Distributed Systems Thinking

**Decision**: Embrace distributed systems; multiple processes coordinated via NATS

**Core Principle**: Components and services are logical concepts. Processes can be native binaries or containers. Deployment tier matches robot complexity.

| Tier | Description | Target |
|------|-------------|--------|
| **Tier 1** | systemd (Simple Robots) | GPS trackers, sensor platforms |
| **Tier 2** | K3s single-node (Complex Robots) | University labs, advanced makers |
| **Tier 3** | K3s multi-node (Fleet Management) | Commercial deployments |

**Why**:
- NATS enables distributed coordination regardless of deployment tier
- Complexity is a liability—add it only when benefits outweigh costs
- Simple robots stay simple (most should be Tier 1)

### 4. Cloud Patterns vs. ROS 2

**Decision**: Embrace cloud-native patterns as competitive advantage

| Cloud Pattern | ROS 2 Approach | Gorai Approach | Advantage |
|---------------|----------------|----------------|-----------|
| Message Broker | DDS peer-to-peer | NATS server | Decouples components, easy monitoring |
| Service Mesh | Custom discovery | NATS queue groups | Automatic load balancing |
| Event Sourcing | rosbag (manual) | JetStream (built-in) | Replay streams, time-travel debug |
| Config Management | Per-node params | NATS KV store | Global config, hot reload |
| Observability | Custom diagnostics | Prometheus /metrics | Industry-standard tools |

**Why**: ROS 2's DDS is pre-cloud (2004 LAN design). We use infrastructure proven at global scale.

---

## Target Market Definition

### Primary Users

- **Makers** — building capable autonomous robots (not toys)
- **Citizen scientists** — monitoring watersheds, marine environments
- **Students/educators** — teaching robotics without PhD toolchains
- **Small organizations** — needing autonomy without enterprise budgets
- **Hobbyists** — want more than Arduino, less complexity than ROS 2

### NOT Target Users

- ❌ Enterprise warehouse robotics → use ROS 2 + AMR vendors
- ❌ Autonomous vehicle research → use ROS 2 + Autoware
- ❌ PhD robotics research → use ROS 2, YARP
- ❌ Defense/aerospace → use certified systems
- ❌ Medical robotics → use certified systems

---

## Messaging Guidelines

### Positioning Statements

**DO say**:
- "Professional robotics for prosumers — without the PhD"
- "Gorai makes professional robotics accessible"
- "We learned from ROS 2's decades of experience"
- "Go core, best tool for each job"
- "Cloud-native patterns proven at scale"

**DON'T say**:
- "Gorai is better than ROS 2" (different markets)
- "Pure Go or nothing" (we're pragmatic)
- "We reinvented everything" (we borrowed proven patterns)
- "ROS 2 is bad" (it's excellent for its target market)

### Competitive Positioning

Not "vs. ROS 2" — different markets:
- **Gorai**: Prosumer, days to productivity, accessible
- **ROS 2**: Enterprise/research, months to mastery, comprehensive

Think: "ROS 2 for prosumers," not "ROS 2 killer"

---

## Development Priorities

### Phase 1: Proof of Concept (Current)

1. ✅ Gorai core (NATS, nodes, pub/sub)
2. ✅ Basic sensors (GPS, IMU, compass) in pure Go
3. ✅ Motor control (I2C/PWM) in pure Go
4. 🔄 Simple vision (webcam, basic detection) in Python
5. 🔄 Web dashboard in Go
6. ⏳ Deploy to PiCar-X as validation

### Phase 2: First Product (Months 6-12)

1. Marine-specific sensors
2. Waypoint navigation
3. Mission planner
4. LoRa telemetry
5. Hardware design finalized
6. ⏸️ Defer: ROS 2 bridge, K3s

### Phase 3: Ecosystem (Months 12-18)

1. ROS 2 bridge MVP (ROS2 → NATS)
2. Gazebo simulation integration
3. Drive (wheeled robot) hardware
4. Community drivers
5. Podman deployment templates

### Phase 4: Advanced (Months 18-24)

1. SLAM integration (Cartographer via C++)
2. K3s edge-cloud deployment
3. Fleet management dashboard
4. Advanced ML (object tracking, SLAM)

**Legend**: ✅ Complete | 🔄 In Progress | ⏳ Planned | ⏸️ Deferred

---

## Project Goals

1. **Target prosumer market** — makers, citizen scientists, students, small organizations
2. **Go core framework** — with pragmatic polyglot services via NATS
3. **Cloud-native patterns** — NATS, Prometheus, JetStream (proven distributed systems)
4. **AI-assisted coding** — designed for modern AI-powered development
5. **Low barrier to entry** — productive in days, not months
6. **ROS 2 compatibility** — bridge planned (Phase 3) for ecosystem access
7. **Modular architecture** — components, services, and resources all use same interface
8. **Edge AI focus** — TPU/NPU acceleration, ONNX Runtime, TensorFlow Lite
9. **No cloud dependency** — standalone-first, cloud-optional
10. **Have fun!** — robotics should be accessible and enjoyable

---

## AI-Assisted Development

Gorai is built entirely with AI assistance. Go's clarity, strong typing, and consistent idioms make it well-suited for AI-assisted development.

We believe AI-assisted software engineering is the future, and the entire project is organized to leverage it to the maximum extent possible: detailed specifications, clear interfaces, and comprehensive documentation that both humans and AI can reason about effectively.
`
  },
  comparison: {
    title: 'Framework Comparison',
    subtitle: 'How Gorai compares to other robotics frameworks',
    icon: '⚖️',
    content: `
## Quick Comparison

| Aspect | Gorai | ROS 2 | Viam | YARP |
|--------|-------|-------|------|------|
| **Target Market** | Prosumer | Enterprise/Research | Cloud-first | Research |
| **Language** | Go + TinyGo | C++/Python | Go | C++ |
| **Middleware** | NATS (cloud-native) | DDS (pre-cloud) | gRPC | Custom carriers |
| **Learning Curve** | Days | Months | Moderate | High |
| **Build System** | Go modules | CMake + ament + colcon | Go modules | CMake |
| **Deployment** | Native + systemd | Workspace sourcing | Cloud-dependent | Build artifacts |
| **AI/ML** | First-class + TPU/NPU | Package ecosystem | First-class | Minimal |
| **MCU Support** | TinyGo | micro-ROS | None | None |
| **ROS 2 Bridge** | Planned (Phase 3) | Native | None | Yes |
| **License** | Apache 2.0 | Apache 2.0 | AGPL | BSD-3 |

---

## Detailed Comparison

### vs. ROS 2

**When to use ROS 2 instead:**
- Enterprise/research robotics (warehouse automation, autonomous vehicles)
- Need for full ROS ecosystem (thousands of packages, simulation, SLAM libraries)
- Academic environment where ROS 2 is the standard
- Have months to invest in learning complex toolchains

**When to use Gorai instead:**
- Building prosumer robots (marine monitoring, research platforms)
- Want to be productive in days, not months
- Prefer modern developer experience with AI-assisted coding
- Need cloud-native patterns (NATS, Prometheus)
- Don't need the full ROS 2 ecosystem

**Technical Differences:**

| Aspect | Gorai | ROS 2 |
|--------|-------|-------|
| Middleware | NATS (designed 2010s, cloud-native) | DDS (designed 2004, enterprise LAN) |
| Discovery | NATS server (simple, firewall-friendly) | DDS multicast (complex, network issues) |
| Build | \`go build\` | CMake + ament + colcon |
| Config | JSON with hot reload | YAML + launch files |
| Observability | Prometheus native | Custom diagnostics |
| Event replay | JetStream built-in | rosbag (separate tool) |

---

### vs. Viam

**When to use Viam instead:**
- Need cloud-first fleet management
- Want commercial support
- Prefer hosted platform

**When to use Gorai instead:**
- Want standalone-first, cloud-optional
- Need microcontroller support (TinyGo)
- Prefer fully open-source (Apache 2.0 vs AGPL)
- Want edge AI focus with TPU/NPU

**Technical Differences:**

| Aspect | Gorai | Viam |
|--------|-------|------|
| Cloud dependency | Optional | Core feature |
| MCU support | TinyGo | None |
| License | Apache 2.0 | AGPL |
| Edge AI | First-class (TPU/NPU) | Cloud-centric |
| ROS 2 interop | Bridge planned | None |

---

### vs. Arduino/MicroPython

**When to use Arduino/MicroPython instead:**
- Simple, single-purpose projects
- Learning embedded programming basics
- Low-cost hardware requirements
- No need for multi-robot coordination

**When to use Gorai instead:**
- Need real autonomy (not just remote control)
- Multi-sensor fusion required
- Fleet coordination needed
- Want to grow beyond educational projects
- Need AI/ML capabilities

---

## Cloud-Native Advantage

The core technical differentiator is applying cloud infrastructure patterns to robotics:

| Cloud Pattern | Traditional Robotics | Gorai Approach | Benefit |
|---------------|---------------------|----------------|---------|
| **Message Broker** | Peer-to-peer | NATS server | Decouples producers/consumers |
| **Service Discovery** | Manual or multicast | NATS built-in | Works through firewalls |
| **Event Sourcing** | Custom logging | JetStream | Replay, time-travel debugging |
| **Configuration** | Per-node files | NATS KV store | Global config, hot reload |
| **Observability** | Custom metrics | Prometheus | Industry-standard tools |
| **Security** | Custom or DDS Security | NATS auth/TLS/JWT | Simple, proven |

---

## Market Positioning

### The Gap We Fill

\`\`\`
Price / Complexity Spectrum:

$100 ─── $500 ─── $1,000 ─── $2,000 ─── $5,000 ─── $10,000+
  │       │         │          │          │          │
  │       │         │          │          │          └─ Enterprise Robotics
  │       │         │          │          │             (ROS 2, industrial)
  │       │         │          │          │
  │       │         │          └──────────┴─ Professional USV
  │       │         │                        ($4,000+ entry)
  │       │         │
  │       │         └─ ★ GORAI TARGET MARKET ★
  │       │            Capable autonomous robots
  │       │            Professional software
  │       │            Accessible price
  │       │
  │       └─ Advanced Maker Projects
  │          (Still limited capability)
  │
  └─ Educational Toys / Basic Kits
     (Limited real-world use)
\`\`\`

### Why This Gap Exists

1. **ROS 2 optimized for enterprise** — complex toolchain assumes dedicated robotics engineers
2. **Commercial USVs serve corporate customers** — pricing reflects enterprise sales cycles
3. **Maker projects lack autonomy** — focus on hardware, software often neglected
4. **Cloud platforms need cloud** — don't work offline or in remote areas

### How Gorai Fills It

1. **Professional capability** at maker prices
2. **Simple deployment** without enterprise infrastructure
3. **Standalone-first** with optional cloud
4. **Modern DX** including AI-assisted development
5. **Open source** with no vendor lock-in

---

## When NOT to Use Gorai

Be honest about limitations:

1. **Enterprise warehouse robotics** → Use ROS 2 + industry AMR solutions
2. **Autonomous vehicles** → Use ROS 2 + Autoware
3. **PhD-level robotics research** → Use ROS 2 for ecosystem access
4. **Defense/aerospace applications** → Use certified, audited systems
5. **Medical robotics** → Use certified, regulated systems
6. **Need specific ROS 2 package today** → Use ROS 2 (our bridge is Phase 3)

We're not trying to replace ROS 2. We're targeting a different market.
`
  }
};

// Sidebar navigation component
const InfoSidebar = ({ currentPath }) => {
  const navItems = [
    { path: '/gorai/info', label: 'Strategic Summary', icon: '📋' },
    { path: '/gorai/info/comparison', label: 'Framework Comparison', icon: '⚖️' },
  ];

  return (
    <aside className="gorai-info-sidebar">
      <div className="gorai-sidebar-header">
        <Link to="/gorai" className="gorai-sidebar-logo">
          <img src="/images/gorai.png" alt="" className="gorai-sidebar-logo-img" />
          <span>Gorai Info</span>
        </Link>
      </div>
      <nav className="gorai-sidebar-nav">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`gorai-sidebar-link ${currentPath === item.path ? 'active' : ''}`}
          >
            <span className="gorai-sidebar-icon">{item.icon}</span>
            <span className="gorai-sidebar-label">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="gorai-sidebar-footer">
        <Link to="/gorai/docs" className="gorai-sidebar-docs-link">
          View Documentation →
        </Link>
      </div>
    </aside>
  );
};

// Info content component
const InfoContent = ({ infoKey }) => {
  const info = infoContent[infoKey];

  if (!info) {
    return <Navigate to="/gorai/info" replace />;
  }

  return (
    <motion.article
      className="gorai-info-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <header className="gorai-info-header">
        <span className="gorai-info-icon">{info.icon}</span>
        <h1>{info.title}</h1>
        <p className="gorai-info-subtitle">{info.subtitle}</p>
      </header>

      <div className="gorai-info-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {info.content}
        </ReactMarkdown>
      </div>
    </motion.article>
  );
};

const GoraiInfoPage = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="gorai-info-page">
      <InfoSidebar currentPath={location.pathname} />
      
      <button 
        className="gorai-mobile-menu-toggle"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? '✕' : '☰'} Menu
      </button>

      {mobileMenuOpen && (
        <div className="gorai-mobile-sidebar" onClick={() => setMobileMenuOpen(false)}>
          <InfoSidebar currentPath={location.pathname} />
        </div>
      )}

      <main className="gorai-info-main">
        <div className="gorai-info-container">
          <Routes>
            <Route index element={<InfoContent infoKey="index" />} />
            <Route path="comparison" element={<InfoContent infoKey="comparison" />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default GoraiInfoPage;

